// TE-2011: lift status vocabulary.
//
// The lifts API (duo-iot-service-lifts, /v2/lifts) stores DUO_LIFT.ACTIVE as 1/0
// and exchanges it as the strings "In Service" / "Out of Service". The same API
// is shared with diot-insights-ui and sku360-ui, which still use those strings,
// so the API contract is left unchanged. IRIS only presents the inactive state
// as "Spare".
//
// Always go through these helpers instead of comparing status strings inline.

// Values sent to / received from the lifts API. Do not change.
export const LIFT_API_STATUS = {
  IN_SERVICE: "In Service",
  OUT_OF_SERVICE: "Out of Service",
};

// Labels shown in the IRIS UI.
export const LIFT_STATUS_LABEL = {
  IN_SERVICE: "In Service",
  SPARE: "Spare",
};

// Colors for the Spare / In Service status text in the grids: the Lift Details
// status toggle and the maintenance history action column. Spare is red here,
// which is what PBC asked for on the grid.
//
// The Lift Map is deliberately different. There, Spare is grey to match its
// pin, because red on the map means "no valid location in over 7 days" and a
// grey pin with red text would read as two states for one lift. The map does
// not use these values; see STATUS_COLORS in LiftMapView.jsx.
export const LIFT_STATUS_COLOR = {
  IN_SERVICE: "#018015",
  SPARE: "#D30000",
};

const normalize = (status) => String(status ?? "").trim().toLowerCase();

/**
 * True when the lift is not in service. Accepts the API value ("Out of Service")
 * and the display label ("Spare"), so it works on raw API data and on rows that
 * have already been mapped for display.
 */
export function isSpareStatus(status) {
  const s = normalize(status);
  return (
    s === normalize(LIFT_API_STATUS.OUT_OF_SERVICE) ||
    s === normalize(LIFT_STATUS_LABEL.SPARE)
  );
}

/** Maps an API status to its IRIS display label. Unknown values pass through. */
export function toLiftStatusLabel(status) {
  return isSpareStatus(status) ? LIFT_STATUS_LABEL.SPARE : status;
}

/**
 * Color for a lift status in the grids: red for Spare, green otherwise.
 * The map does not use this — it colors Spare grey to match the pin.
 */
export function liftStatusColor(status) {
  return isSpareStatus(status) ? LIFT_STATUS_COLOR.SPARE : LIFT_STATUS_COLOR.IN_SERVICE;
}

/** The API status to send when the user toggles a lift away from `currentStatus`. */
export function nextLiftApiStatus(currentStatus) {
  return isSpareStatus(currentStatus)
    ? LIFT_API_STATUS.IN_SERVICE
    : LIFT_API_STATUS.OUT_OF_SERVICE;
}
